import {
  CAROUSEL_HTML_MARKER,
  EMBED_PLACEHOLDER_HTML,
} from "../constants/carouselConstants";
import { stripParagraphTags } from "./previewUtils";

export const isCarouselEmbedHtml = (html?: string) => {
  if (typeof html !== "string") {
    return false;
  }

  if (
    html.includes(CAROUSEL_HTML_MARKER) ||
    html.includes('data-sb-email-embed="true"') ||
    html.includes('data-sb-email-embed-placeholder="true"')
  ) {
    return true;
  }

  const normalized = html.toLowerCase();
  if (
    normalized.includes("scroll to see more products") &&
    normalized.includes('role="presentation"')
  ) {
    return true;
  }

  // Live component iframe embed (copied from Landing Page → Embed to Email)
  if (normalized.includes("<iframe") && normalized.includes("/component/")) {
    return true;
  }

  return false;
};

/** Resize helper copied with iframe embeds; Quill may strip tags and leave this as plain text. */
const CAROUSEL_EMBED_RESIZE_SCRIPT =
  /(?:<script[^>]*>)?\s*\(function\(\)\{var f=document\.currentScript\.previousElementSibling[\s\S]*?\}\)\(\);?\s*(?:<\/script>)?/gi;

export const stripOrphanedCarouselResizeScripts = (html?: string): string => {
  if (!html) return "";
  return html.replace(CAROUSEL_EMBED_RESIZE_SCRIPT, "").trim();
};

const isCarouselIframe = (el: Element): boolean => {
  if (el.tagName !== "IFRAME") return false;
  const iframe = el as HTMLIFrameElement;
  const src = (iframe.getAttribute("src") || "").toLowerCase();
  return (
    src.includes("/component/") || iframe.hasAttribute("data-sb-email-embed")
  );
};

/** Iframe embeds from the landing page include a resize helper script immediately after. */
const getIframeEmbedBlockHtml = (iframe: HTMLIFrameElement): string => {
  const parts = [iframe.outerHTML];
  const next = iframe.nextElementSibling;
  if (next?.tagName === "SCRIPT") {
    parts.push(next.outerHTML);
  }
  return parts.join("");
};

const collectDivCarouselEmbeds = (
  container: HTMLElement,
  sourceHtml: string
): HTMLElement[] => {
  let embeds: HTMLElement[] = Array.from(
    container.querySelectorAll('[data-sb-email-embed="true"]')
  ) as HTMLElement[];

  if (embeds.length === 0) {
    container.querySelectorAll("div").forEach((div) => {
      const divEl = div as HTMLElement;
      const contentEditable = divEl.getAttribute("contenteditable");
      const text = (divEl.textContent || "").toLowerCase();
      const hasCarouselMarker = sourceHtml.includes(CAROUSEL_HTML_MARKER);

      if (
        (contentEditable === "false" &&
          text.includes("scroll to see more products")) ||
        (hasCarouselMarker &&
          divEl.innerHTML.includes(CAROUSEL_HTML_MARKER)) ||
        divEl.innerHTML.includes('data-sb-email-embed="true"')
      ) {
        divEl.setAttribute("data-sb-email-embed", "true");
        embeds.push(divEl);
      }
    });
  }

  return embeds;
};

/** Split combined email HTML into Quill text and carousel embed blocks. */
export const partitionEmailBody = (
  html?: string
): { textOnly: string; embeds: string[] } => {
  if (!html || typeof document === "undefined") {
    return { textOnly: stripOrphanedCarouselResizeScripts(html || ""), embeds: [] };
  }

  try {
    const container = document.createElement("div");
    container.innerHTML = html;
    const embedBlocks: string[] = [];

    // Live iframe embeds first (keep paired resize script)
    Array.from(container.querySelectorAll("iframe"))
      .filter(isCarouselIframe)
      .forEach((iframe) => {
        embedBlocks.push(getIframeEmbedBlockHtml(iframe as HTMLIFrameElement));
        const script = iframe.nextElementSibling;
        iframe.remove();
        if (script?.tagName === "SCRIPT") {
          script.remove();
        }
      });

    // Orphan resize scripts left after Quill stripped the iframe
    Array.from(container.querySelectorAll("script")).forEach((script) => {
      const content = script.textContent || script.innerHTML || "";
      if (content.includes("document.currentScript.previousElementSibling")) {
        script.remove();
      }
    });

    const divEmbeds = collectDivCarouselEmbeds(container, html);
    divEmbeds.forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (!htmlEl.hasAttribute("data-sb-email-embed")) {
        htmlEl.setAttribute("data-sb-email-embed", "true");
      }
      embedBlocks.push(htmlEl.outerHTML);
      el.remove();
    });

    return {
      textOnly: stripOrphanedCarouselResizeScripts(container.innerHTML.trim()),
      embeds: embedBlocks,
    };
  } catch (error) {
    console.error("Error partitioning email body:", error);
    return {
      textOnly: stripOrphanedCarouselResizeScripts(html || ""),
      embeds: [],
    };
  }
};

export const markCarouselHtml = (html: string) => {
  if (!html) return html;
  if (html.includes('data-sb-email-embed="true"')) return html;
  if (/<iframe\b/i.test(html)) {
    return html.replace(/<iframe\b/i, '<iframe data-sb-email-embed="true"');
  }
  return html.replace(/<div/i, '<div data-sb-email-embed="true"');
};

export const normalizeCarouselHtml = (html?: string): { normalizedHtml: string; embedHtml: string | null } => {
  if (!html) {
    return { normalizedHtml: "", embedHtml: null };
  }

  try {
    if (typeof document === "undefined") {
      return { normalizedHtml: html, embedHtml: null };
    }
    const container = document.createElement("div");
    container.innerHTML = html;

    let node = container.querySelector('[data-sb-email-embed="true"]') as HTMLElement | null;

    if (!node) {
      const iframe = Array.from(container.querySelectorAll("iframe")).find(
        isCarouselIframe
      ) as HTMLIFrameElement | undefined;
      if (iframe) {
        const embedHtml = getIframeEmbedBlockHtml(iframe);
        const script = iframe.nextElementSibling;
        iframe.remove();
        if (script?.tagName === "SCRIPT") {
          script.remove();
        }
        return { normalizedHtml: container.innerHTML, embedHtml };
      }
    }

    if (!node) {
      const candidate = Array.from(container.querySelectorAll("div")).find((el) => {
        const div = el as HTMLElement;
        const contentEditable = div.getAttribute("contenteditable");
        const text = (div.textContent || "").toLowerCase();
        return (
          contentEditable === "false" &&
          text.includes("scroll to see more products")
        );
      }) as HTMLElement | undefined;
      if (candidate) {
        candidate.setAttribute("data-sb-email-embed", "true");
        node = candidate;
      }
    }

    const embedHtml = node ? node.outerHTML : null;
    return { normalizedHtml: container.innerHTML, embedHtml };
  } catch {
    return { normalizedHtml: html, embedHtml: null };
  }
};

export const extractCarouselEmbed = (html?: string): string | null =>
  normalizeCarouselHtml(html).embedHtml;

// Extract ALL embed blocks from HTML (for multiple embeds)
export const extractAllCarouselEmbeds = (html?: string): string[] =>
  partitionEmailBody(html).embeds;

// Remove all embed blocks and return only text content
export const removeAllEmbedsFromHtml = (html?: string): string =>
  partitionEmailBody(html).textOnly;

export const replaceEmbedWithPlaceholder = (html: string, placeholderHtml: string) => {
  const embed = extractCarouselEmbed(html);
  if (!embed) {
    return { displayHtml: html, embedHtml: null };
  }
  return {
    displayHtml: html.replace(embed, placeholderHtml),
    embedHtml: embed,
  };
};

export const restoreEmbedFromPlaceholder = (html: string, embedHtml: string | null) => {
  if (!embedHtml) return html;
  if (html.includes('data-sb-email-embed-placeholder="true"')) {
    return html.replace(
      /<p[^>]*data-sb-email-embed-placeholder="true"[^>]*>[\s\S]*?<\/p>/i,
      embedHtml
    );
  }
  return html;
};

export const getEditorDisplayHtml = (body: string, embedHtml: string | null) => {
  if (!embedHtml) {
    return body || "";
  }
  const { displayHtml } = replaceEmbedWithPlaceholder(body || "", EMBED_PLACEHOLDER_HTML);
  return displayHtml;
};

export const appendCarouselHtml = (baseHtml: string, carouselHtml: string) => {
  const addition = markCarouselHtml((carouselHtml || "").trim());
  if (!addition) {
    return baseHtml || "";
  }

  const baseWithoutTrailingEmptyParas = (baseHtml || "").replace(/(<p><br><\/p>)+$/gi, "");
  if (!baseWithoutTrailingEmptyParas.trim()) {
    return addition;
  }

  const spacer = "<div style=\"margin: 24px 0;\"></div>";
  return `${baseWithoutTrailingEmptyParas}${spacer}${addition}`;
};

export const combineMultipleEmbeds = (embeds: string[]): string => {
  if (!embeds || embeds.length === 0) return "";
  
  // Mark each embed and ensure it has the data attribute
  const markedEmbeds = embeds
    .map(embed => {
      const trimmed = embed.trim();
      if (!trimmed) return null;
      // Ensure the embed has the marker
      return markCarouselHtml(trimmed);
    })
    .filter((embed): embed is string => !!embed);
  
  if (markedEmbeds.length === 0) return "";
  if (markedEmbeds.length === 1) return markedEmbeds[0];
  
  // Join multiple embeds with spacers
  const spacer = "<div style=\"margin: 24px 0;\"></div>";
  return markedEmbeds.join(spacer);
};

/** Collect carousel embed blocks from modal paste areas. */
export const collectEmbedsFromCodeAreas = (areas: string[]): string[] => {
  const found: string[] = [];
  (areas || []).forEach((area) => {
    const trimmedArea = (area || "").trim();
    if (!trimmedArea) return;

    const extracted = extractAllCarouselEmbeds(trimmedArea);
    if (extracted.length > 0) {
      extracted.forEach((embed) => {
        const t = embed.trim();
        if (t && !found.some((e) => e.trim() === t)) found.push(embed);
      });
      return;
    }

    const single =
      extractCarouselEmbed(trimmedArea) ||
      (isCarouselEmbedHtml(trimmedArea) ? trimmedArea : null);
    if (single) {
      const t = single.trim();
      if (t && !found.some((e) => e.trim() === t)) found.push(t);
    }
  });
  return found;
};

/** Build the full email body from Quill text + stored embed blocks. */
export const buildCombinedEmailBody = (
  textHtml: string,
  embeds: string[]
): string => {
  const cleanedText = stripParagraphTags(textHtml || "");
  const combinedEmbeds = combineMultipleEmbeds(embeds || []);
  if (!combinedEmbeds) {
    return cleanedText;
  }
  return appendCarouselHtml(cleanedText, combinedEmbeds);
};

export const insertCarouselHtml = (container: HTMLElement | null, carouselHtml: string) => {
  const addition = markCarouselHtml((carouselHtml || "").trim());
  if (!addition) {
    return container?.innerHTML || "";
  }

  if (!container) {
    return appendCarouselHtml("", addition);
  }

  const doc = container.ownerDocument || document;
  const selection = doc.getSelection?.();
  const baseHtml = container.innerHTML;

  if (!selection || selection.rangeCount === 0) {
    return appendCarouselHtml(baseHtml, addition);
  }

  const range = selection.getRangeAt(0);
  if (!container.contains(range.startContainer)) {
    return appendCarouselHtml(baseHtml, addition);
  }

  range.deleteContents();

  const precedingRange = range.cloneRange();
  precedingRange.selectNodeContents(container);
  precedingRange.setEnd(range.startContainer, range.startOffset);
  const precedingFragment = precedingRange.cloneContents();
  const tempHolder = doc.createElement("div");
  tempHolder.appendChild(precedingFragment);
  const hasPrecedingContent = Boolean(tempHolder.textContent?.trim());

  const fragment = doc.createDocumentFragment();
  if (hasPrecedingContent) {
    const spacerNode = doc.createElement("div");
    spacerNode.setAttribute("style", "margin: 24px 0;");
    fragment.appendChild(spacerNode);
  }

  const wrapper = doc.createElement("div");
  wrapper.innerHTML = addition;
  const insertedNodes: ChildNode[] = [];
  while (wrapper.firstChild) {
    const node = wrapper.firstChild as ChildNode;
    insertedNodes.push(node);
    fragment.appendChild(node);
  }

  const lastInsertedNode = insertedNodes.length
    ? insertedNodes[insertedNodes.length - 1]
    : fragment.lastChild;

  range.insertNode(fragment);

  if (lastInsertedNode) {
    range.setStartAfter(lastInsertedNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  return container.innerHTML;
};
